import { useRef, useCallback } from 'react';
import { useAppStore } from '../store/appStore';
import { startBuildSimulation } from '../services/buildService';

export function useBuild() {
  const cancelRef = useRef<(() => void) | null>(null);

  const {
    buildStatus, buildProgress, buildPhase, buildSettings, buildLogs,
    setBuildStatus, setBuildProgress, setBuildPhase,
    addLog, clearLogs, incrementBuilds,
  } = useAppStore();

  const startBuild = useCallback(() => {
    if (buildStatus === 'building') return;

    clearLogs();
    setBuildStatus('building');
    setBuildProgress(0);

    const cancel = startBuildSimulation(
      (progress, phase) => {
        setBuildProgress(progress);
        setBuildPhase(phase);
      },
      (entry) => addLog(entry),
      () => {
        setBuildStatus('success');
        incrementBuilds();
        cancelRef.current = null;
      }
    );

    cancelRef.current = cancel;
  }, [buildStatus, clearLogs, setBuildStatus, setBuildProgress, setBuildPhase, addLog, incrementBuilds]);

  const cancelBuild = useCallback(() => {
    cancelRef.current?.();
    cancelRef.current = null;
    setBuildStatus('idle');
    setBuildProgress(0);
    addLog({ type: 'error', text: '✕ Build cancelled by user.' });
  }, [setBuildStatus, setBuildProgress, addLog]);

  return {
    buildStatus,
    buildProgress,
    buildPhase,
    buildSettings,
    buildLogs,
    startBuild,
    cancelBuild,
    isBuilding: buildStatus === 'building',
    isSuccess: buildStatus === 'success',
  };
}
